import { useMemo } from "react";

import {
  LookupIcon,
  MapAreaIcon,
  ProfilingIcon,
  StorageIcon
} from "@/assets/icons";
import Divider from "@/components/ui/Divider";
import { AnalysisJobAreaInputType, AnalysisJobItem } from "@/types/analysis";
import { AnalysisResultType } from "@/types/dataset";

import dayjs from "dayjs";
import { twMerge } from "tailwind-merge";

interface HistoryCardProps {
  data: AnalysisJobItem;
  isSelected?: boolean;
  onSelect?: (id: string) => void;
}

export const HistoryCard = ({
  data,
  isSelected = false,
  onSelect
}: HistoryCardProps) => {
  const datasetCount = data.datasets?.length || 0;
  const isProfiling =
    data.output_type === AnalysisResultType.TYPE_SITE_PROFILING;

  const resultText = useMemo(() => {
    if (!data?.output_type) return "-";
    return isProfiling ? "Profiling" : "Grid";
  }, [data?.output_type, isProfiling]);

  const typeText = useMemo(() => {
    if (!data?.input_type) return "-";
    return data.input_type === AnalysisJobAreaInputType.ADMIN
      ? "Adm Area"
      : "Catchment";
  }, [data?.input_type]);

  return (
    <button
      data-testid="history-card"
      className={twMerge(
        "relative w-full text-left rounded-lg h-auto border bg-white p-2 group transition-all",
        isSelected
          ? "border-teal-200 bg-teal-50"
          : "border-gainsboro hover:bg-teal-50 hover:border-teal-200"
      )}
      onClick={() => onSelect?.(data.id)}
    >
      <div className="py-2 px-1 overflow-hidden hover:cursor-pointer">
        <div className="flex justify-between items-start">
          <div className="flex flex-col gap-1">
            <p className="text-[10px] text-txtPlaceholder font-normal">
              {data?.created_at
                ? dayjs(data.created_at).format("DD MMM YYYY HH:mm:ss")
                : ""}
              {data.created_by && <span> - by {data.created_by}</span>}{" "}
            </p>

            <p
              className={twMerge(
                "font-bold line-clamp-2 group-hover:text-teal-500",
                isSelected ? "text-teal-500" : "text-txtHeading"
              )}
              title={data.name}
            >
              {data.name || `Spatial Analysis (${data.id})`}
            </p>
          </div>
        </div>

        <Divider
          className="bg-white-smoke w-full h-[1px] my-2"
          lineClassName={twMerge(
            "group-hover:border-teal-200",
            isSelected && "border-teal-200"
          )}
        />

        <div className="flex">
          <div className="w-1/3 flex items-center gap-2">
            <StorageIcon width={16} className="fill-purple-500" />
            <span className="text-[10px]">
              {`${datasetCount} dataset${datasetCount <= 1 ? "" : "s"}`}
            </span>
          </div>

          <div className="w-1/3 flex items-center gap-2">
            <MapAreaIcon width={16} />
            <span className="text-[10px]">{typeText}</span>
          </div>

          <div className="w-1/3 flex items-center gap-2">
            {isProfiling ? (
              <ProfilingIcon width={14} className="fill-teal-500" />
            ) : (
              <LookupIcon width={14} className="fill-teal-500" />
            )}
            <span className="text-[10px]">{resultText}</span>
          </div>
        </div>
      </div>
    </button>
  );
};
